// News Desk, twice an hour at :09 and :39 for two minutes (Oct 27, Jared, Q15; was a minute at every :x9): three pages
// local, three national and world, ranked by the sidecar (outlets carrying a story, front-page order, local beats), then
// tech. The first two of each carry a line under each headline; the third is headlines only. Also Headlines, the
// loop's screen: a local page and a national page, so national news airs at night and on weekends too.
import { registerDisplay } from '../navigation.mjs';
import { escapeHtml } from '../utils/string.mjs';
import FeatureDisplay from './feature-display.mjs';
import SnapshotDisplay from './snapshot-display.mjs';
import { data, fetchedAt } from './snapshot.mjs';
import { registerFeature } from './scheduler.mjs';
import { asOf } from './format.mjs';

// stories not already used, in rank order
const take = (list, count, used) => {
	// (a snapshot from before Oct 26 held lists per source; those entries have no title and are skipped)
	const out = (list ?? []).filter((s) => typeof s?.title === 'string' && !used.has(s.title)).slice(0, count);
	out.forEach((s) => used.add(s.title));
	return out;
};

const story = (item, { line = true } = {}) => `<div class="it">
	<div class="src">${escapeHtml(item.source)}</div>
	<div><div class="h">${escapeHtml(item.title)}</div>${line && item.line ? `<div class="d">${escapeHtml(item.line)}</div>` : ''}</div>
</div>`;

const page = (title, header, items, now, { seconds = 15, line = true } = {}) => ({
	seconds,
	title,
	html: `<div class="hdr">${header}</div><div class="news${line ? '' : ' headlines'}">${items.map((i) => story(i, { line })).join('')}</div>
		<div class="asof">${asOf(fetchedAt('news'), now)}</div>`,
});

class NewsDesk extends FeatureDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, 'News Desk');
	}

	// eslint-disable-next-line class-methods-use-this
	hasData() {
		return !!data('news')?.local?.length;
	}

	// eslint-disable-next-line class-methods-use-this
	buildPages(now) {
		const news = data('news');
		if (!news) return [];
		const used = new Set();
		const localTop = take(news.local, 3, used);
		const localMore = take(news.local, 3, used);
		const localRest = take(news.local, 3, used);
		const worldTop = take(news.national, 3, used);
		const worldMore = take(news.national, 3, used);
		const worldRest = take(news.national, 3, used);
		const tech = (news.tech ?? []).filter((s) => typeof s?.title === 'string').slice(0, 3);
		// about 100 s of pages, so the intro, the pages and the outro fit the two minutes before Views at :11 and :41
		return [
			localTop.length ? page(['News Desk', 'Local'], 'Local &middot; top stories', localTop, now, { seconds: 16 }) : null,
			localMore.length ? page(['News Desk', 'Local'], 'Local &middot; more', localMore, now, { seconds: 15 }) : null,
			localRest.length ? page(['News Desk', 'Local'], 'Local &middot; also', localRest, now, { seconds: 11, line: false }) : null,
			worldTop.length ? page(['News Desk', 'National'], 'National &amp; world', worldTop, now, { seconds: 16 }) : null,
			worldMore.length ? page(['News Desk', 'National'], 'National &amp; world &middot; more', worldMore, now, { seconds: 15 }) : null,
			worldRest.length ? page(['News Desk', 'World'], 'National &amp; world &middot; also', worldRest, now, { seconds: 11, line: false }) : null,
			tech.length ? page(['News Desk', 'Tech'], 'Tech &middot; Hacker News', tech, now, { seconds: 14, line: false }) : null,
		].filter(Boolean);
	}
}

// the loop's Headlines: three local titles, then three national, no second lines
class Headlines extends SnapshotDisplay {
	constructor(navId, elemId) {
		super(navId, elemId, 'Headlines', { seconds: 18 });
		this.timing.baseDelay = 1000;
		this.timing.delay = [12, 12];
		this.resetNavBaseCount();
		this.calcNavTiming();
	}

	// eslint-disable-next-line class-methods-use-this
	hasData() {
		return !!(data('news')?.local?.length || data('news')?.national?.length);
	}

	render(now) {
		const news = data('news');
		const national = this.screenIndex >= 1;
		const items = (national ? news.national : news.local)?.filter((s) => typeof s?.title === 'string').slice(0, 3) ?? [];
		return `<div class="hdr">${national ? 'National &amp; world' : 'Local'}</div><div class="news headlines">${items.map((i) => story(i, { line: false })).join('')}</div>
			<div class="asof">${asOf(fetchedAt('news'), now)}</div>`;
	}
}

registerDisplay(new Headlines(21, 'headlines'));
const display = new NewsDesk(24, 'news-desk');
registerDisplay(display);
registerFeature('news-desk', display);
